import React, { useEffect, useState } from 'react';
import { ChevronLeft, Phone, Mail, MessageCircle, ChevronDown, ChevronUp, Clock, CheckCircle2, XCircle, AlertCircle, Plus, X } from 'lucide-react';
import { BottomNav } from '../components/BottomNav';
interface DeveloperOfficePageProps {
  developerId: number;
  developerName: string;
  tier?: 'gold' | 'silver' | 'bronze';
  onBack: () => void;
  onRequestClick?: (requestId: number) => void;
  onProjectClick?: (projectId: number) => void;
}
interface Request {
  id: number;
  code: string;
  title: string;
  description: string;
  date: string;
  status: 'pending' | 'approved' | 'rejected' | 'in-progress';
}
interface Project {
  id: number;
  name: string;
  location: string;
  units: number;
  image: string;
}
export function DeveloperOfficePage({
  developerId,
  developerName,
  tier,
  onBack,
  onRequestClick,
  onProjectClick
}: DeveloperOfficePageProps) {
  const [expandedRequestsSection, setExpandedRequestsSection] = useState(true);
  const [expandedProjectsSection, setExpandedProjectsSection] = useState(true);
  const [showCreateTicketModal, setShowCreateTicketModal] = useState(false);
  const [newTicket, setNewTicket] = useState({
    projectId: '',
    title: '',
    description: ''
  });
  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  // Map developer names to their logos
  const getDeveloperLogo = (name: string) => {
    const logoMap: Record<string, string> = {
      'Mountain View': 'https://cdn.magicpatterns.com/uploads/ht2zed5WJfpibcGTs2kKAi/Mountain-View-Logo-Vector.svg-.png',
      'Palm Hills': 'https://cdn.magicpatterns.com/uploads/e3xetC89TNSJtFzKEK9GJ9/OIP.McQBqob_yPFZovOPZDod3AHaHa.webp',
      'Hyde Park': 'https://cdn.magicpatterns.com/uploads/8vQhTfMrXW8bwweERT8mA7/OIP.xWHw7TazT7rFveGb0a9zNwHaHa.webp',
      'Emaar Misr': 'https://cdn.magicpatterns.com/uploads/wijYmcxqmndw1XGyexVnGN/Emaar-Misr-Logo.png',
      Sodic: 'https://cdn.magicpatterns.com/uploads/m7ECvP64WQu5Q5fDGkVyT2/OIP.heFoNUDluvlfTF84mNzC8AHaB6.webp',
      'Ora Developers': 'https://cdn.magicpatterns.com/uploads/qiVDQQJwbWPnXu1qEPHnnJ/1655298523997.jpg',
      'Tatweer Misr': 'https://cdn.magicpatterns.com/uploads/vFd4y3e6VTvKmbjouHn6Df/OIP.WU0po8LB04ePZzUToFmyKQHaD2.webp',
      Inertia: 'https://cdn.magicpatterns.com/uploads/gtoHa4YFoGm69rL7tSXhd6/OIP.8_yr1_3JwTWhYPh-6mszrAHaBG.webp'
    };
    return logoMap[name] || 'https://cdn.magicpatterns.com/uploads/ht2zed5WJfpibcGTs2kKAi/Mountain-View-Logo-Vector.svg-.png';
  };
  // Mock account manager data
  const accountManager = {
    name: 'Mohamed Hossam',
    position: 'Agent',
    phone: '+20 123 456 7890',
    email: 'mohamed.hossam@developer.com',
    avatar: 'https://cdn.magicpatterns.com/uploads/dmyzTWp4qVkkavD8xUDYZW/OIP.g0qYLq0G09unEJPan_GKvwHaHw.webp',
    initials: 'MH'
  };
  // Mock requests data with 6-digit codes
  const [requests, setRequests] = useState<Request[]>([{
    id: 1,
    code: '762532',
    title: 'Unit Reservation Request',
    description: 'Villa A-105, New Cairo Project',
    date: '2 hours ago',
    status: 'pending'
  }, {
    id: 2,
    code: '891234',
    title: 'Price Inquiry',
    description: 'Apartment B-203, Waterway Project',
    date: 'Yesterday',
    status: 'approved'
  }, {
    id: 3,
    code: '456789',
    title: 'Site Visit Request',
    description: 'Hyde Park Project - Multiple Units',
    date: '2 days ago',
    status: 'in-progress'
  }, {
    id: 4,
    code: '123456',
    title: 'Payment Plan Request',
    description: 'Villa C-301, New Cairo Project',
    date: '3 days ago',
    status: 'rejected'
  }]);
  // Mock projects data with Unsplash images
  const projects: Project[] = [{
    id: 1,
    name: 'New Cairo Villas',
    location: 'New Cairo, 5th Settlement',
    units: 120,
    image: 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=400&h=300&fit=crop'
  }, {
    id: 2,
    name: 'Waterway Apartments',
    location: 'New Cairo, Waterway',
    units: 85,
    image: 'https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?w=400&h=300&fit=crop'
  }, {
    id: 3,
    name: 'Hyde Park Residences',
    location: 'New Cairo, Hyde Park',
    units: 200,
    image: 'https://images.unsplash.com/photo-1512917774080-9991f1c4c750?w=400&h=300&fit=crop'
  }, {
    id: 4,
    name: 'Madinaty Towers',
    location: 'New Cairo, Madinaty',
    units: 150,
    image: 'https://images.unsplash.com/photo-1580587771525-78b9dba3b914?w=400&h=300&fit=crop'
  }];
  const getStatusConfig = (status: Request['status']) => {
    const configs = {
      pending: {
        label: 'Pending',
        icon: Clock,
        color: 'text-yellow-600',
        bgColor: 'bg-yellow-50',
        borderColor: 'border-yellow-200'
      },
      approved: {
        label: 'Approved',
        icon: CheckCircle2,
        color: 'text-green-600',
        bgColor: 'bg-green-50',
        borderColor: 'border-green-200'
      },
      rejected: {
        label: 'Rejected',
        icon: XCircle,
        color: 'text-red-600',
        bgColor: 'bg-red-50',
        borderColor: 'border-red-200'
      },
      'in-progress': {
        label: 'In Progress',
        icon: AlertCircle,
        color: 'text-blue-600',
        bgColor: 'bg-blue-50',
        borderColor: 'border-blue-200'
      }
    };
    return configs[status];
  };
  const getTierBadge = () => {
    if (!tier) return null;
    const tierConfig = {
      gold: {
        label: 'GOLD',
        bg: 'bg-yellow-600',
        text: 'text-white'
      },
      silver: {
        label: 'SILVER',
        bg: 'bg-gray-400',
        text: 'text-white'
      },
      bronze: {
        label: 'BRONZE',
        bg: 'bg-orange-600',
        text: 'text-white'
      }
    };
    const config = tierConfig[tier];
    return <span className={`${config.bg} ${config.text} text-[10px] font-bold px-2 py-1 rounded`} data-id="element-948">
        {config.label}
      </span>;
  };
  const handleRequestCardClick = (requestId: number) => {
    if (onRequestClick) {
      onRequestClick(requestId);
    }
  };
  const handleCreateTicket = () => {
    if (newTicket.projectId && newTicket.title && newTicket.description) {
      const selectedProject = projects.find(p => p.id === parseInt(newTicket.projectId));
      const newRequest: Request = {
        id: requests.length + 1,
        code: Math.floor(100000 + Math.random() * 900000).toString(),
        title: newTicket.title,
        description: `${selectedProject?.name} - ${newTicket.description}`,
        date: 'Just now',
        status: 'pending'
      };
      setRequests([newRequest, ...requests]);
      setShowCreateTicketModal(false);
      setNewTicket({
        projectId: '',
        title: '',
        description: ''
      });
    }
  };
  return <div className="min-h-screen bg-neutral-bg pb-20 font-sans w-full" data-id="element-949">
      <div className="w-full mx-auto bg-neutral-bg min-h-screen relative" data-id="element-950">
        {/* Header with Logo */}
        <div className="bg-primary px-4 py-4 safe-top flex items-center justify-between sticky top-0 z-10 shadow-sm" data-id="element-951">
          <button onClick={onBack} className="p-2 -ml-2 text-white hover:bg-white/10 rounded-full transition-colors" data-id="element-952">
            <ChevronLeft className="w-6 h-6" data-id="element-953" />
          </button>
          <div className="flex items-center gap-3" data-id="element-954">
            <div className="w-8 h-8 bg-white rounded-full p-1.5 flex items-center justify-center" data-id="element-955">
              <img src={getDeveloperLogo(developerName)} alt={developerName} className="w-full h-full object-contain" data-id="element-956" />
            </div>
            <h1 className="font-semibold text-lg text-white" data-id="element-957">
              {developerName}
            </h1>
          </div>
          <div className="w-10" data-id="element-958"></div>
        </div>

        <div className="space-y-6 pb-4" data-id="element-959">
          {/* Company Info Card */}
          <section className="px-4 pt-4" data-id="element-960">
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6" data-id="element-961">
              <div className="flex items-start justify-between mb-4" data-id="element-962">
                {/* Company Logo */}
                <div className="w-20 h-20 flex items-center justify-center bg-white rounded-lg p-2" data-id="element-963">
                  <img src={getDeveloperLogo(developerName)} alt={developerName} className="w-full h-full object-contain" data-id="element-964" />
                </div>
                {getTierBadge()}
              </div>

              <h2 className="text-xl font-bold text-neutral-text mb-1" data-id="element-965">
                {developerName}
              </h2>
              <p className="text-sm text-gray-500 mb-4" data-id="element-966">
                Real Estate Developer
              </p>
            </div>
          </section>

          {/* Account Manager Card */}
          <section className="px-4" data-id="element-967">
            <div className="bg-gradient-to-r from-teal-100 via-blue-50 to-purple-100 rounded-2xl p-6 shadow-sm" data-id="element-968">
              <div className="flex items-center justify-between" data-id="element-969">
                <div className="flex items-center gap-4" data-id="element-970">
                  {/* Profile Image with Accent Border */}
                  <div className="relative" data-id="element-971">
                    <div className="w-20 h-20 rounded-full border-4 border-accent overflow-hidden bg-white" data-id="element-972">
                      <img src={accountManager.avatar} alt={accountManager.name} className="w-full h-full object-cover" data-id="element-973" />
                    </div>
                  </div>

                  {/* Name and Title */}
                  <div data-id="element-974">
                    <p className="text-sm font-medium text-primary mb-1" data-id="element-975">
                      {accountManager.position}
                    </p>
                    <h3 className="text-xl font-bold text-primary" data-id="element-976">
                      {accountManager.name}
                    </h3>
                  </div>
                </div>

                {/* Contact Actions */}
                <div className="flex gap-3" data-id="element-977">
                  <button className="w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-md hover:shadow-lg transition-shadow" data-id="element-978">
                    <Phone className="w-6 h-6 text-gray-700" data-id="element-979" />
                  </button>
                  <button className="w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-md hover:shadow-lg transition-shadow" data-id="element-980">
                    <MessageCircle className="w-6 h-6 text-gray-700" data-id="element-981" />
                  </button>
                </div>
              </div>
            </div>
          </section>

          {/* Requests Section */}
          <section className="px-4" data-id="element-982">
            <div className="flex items-center justify-between mb-3" data-id="element-983">
              <button onClick={() => setExpandedRequestsSection(!expandedRequestsSection)} className="flex items-center gap-2 px-2 py-1 hover:bg-white/50 rounded-lg transition-colors" data-id="element-984">
                <h3 className="text-sm font-bold text-neutral-text uppercase tracking-wide" data-id="element-985">
                  Requests
                </h3>
                <span className="bg-secondary/20 text-secondary text-xs font-bold px-2 py-0.5 rounded-full" data-id="element-986">
                  {requests.length}
                </span>
                {expandedRequestsSection ? <ChevronUp className="w-4 h-4 text-gray-400" data-id="element-987" /> : <ChevronDown className="w-4 h-4 text-gray-400" data-id="element-988" />}
              </button>

              <button onClick={() => setShowCreateTicketModal(true)} className="flex items-center gap-1 bg-accent hover:bg-accent-dark text-primary font-semibold text-sm px-4 py-2 rounded-lg transition-colors" data-id="element-989">
                <Plus className="w-4 h-4" data-id="element-990" />
                New Ticket
              </button>
            </div>

            {expandedRequestsSection && <div className="space-y-3" data-id="element-991">
                {requests.map(request => {
              const statusConfig = getStatusConfig(request.status);
              const StatusIcon = statusConfig.icon;
              return <button key={request.id} onClick={() => handleRequestCardClick(request.id)} className={`w-full bg-white rounded-lg border ${statusConfig.borderColor} p-4 shadow-sm hover:shadow-md transition-shadow text-left`} data-id="element-992">
                      <div className="flex items-start justify-between mb-2" data-id="element-993">
                        <div className="flex-1" data-id="element-994">
                          <p className="text-xs font-semibold text-secondary mb-1" data-id="element-995">
                            Request No. #{request.code}
                          </p>
                          <h4 className="font-semibold text-neutral-text text-sm" data-id="element-996">
                            {request.title}
                          </h4>
                        </div>
                        <span className={`flex items-center gap-1 ${statusConfig.bgColor} ${statusConfig.color} text-xs font-semibold px-2 py-1 rounded-full ml-2`} data-id="element-997">
                          <StatusIcon className="w-3 h-3" data-id="element-998" />
                          {statusConfig.label}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 mb-2" data-id="element-999">
                        {request.description}
                      </p>
                      <p className="text-xs text-gray-400" data-id="element-1000">{request.date}</p>
                    </button>;
            })}
              </div>}
          </section>

          {/* Projects Section */}
          <section className="px-4" data-id="element-1001">
            <button onClick={() => setExpandedProjectsSection(!expandedProjectsSection)} className="w-full flex items-center justify-between mb-3 px-2 py-1 hover:bg-white/50 rounded-lg transition-colors" data-id="element-1002">
              <div className="flex items-center gap-2" data-id="element-1003">
                <h3 className="text-sm font-bold text-neutral-text uppercase tracking-wide" data-id="element-1004">
                  Projects
                </h3>
                <span className="bg-accent/20 text-accent-dark text-xs font-bold px-2 py-0.5 rounded-full" data-id="element-1005">
                  {projects.length}
                </span>
              </div>
              {expandedProjectsSection ? <ChevronUp className="w-4 h-4 text-gray-400" data-id="element-1006" /> : <ChevronDown className="w-4 h-4 text-gray-400" data-id="element-1007" />}
            </button>

            {expandedProjectsSection && <div className="grid grid-cols-2 gap-3" data-id="element-1008">
                {projects.map(project => <div key={project.id} className="bg-white rounded-lg border border-gray-100 shadow-sm overflow-hidden" data-id="element-1009">
                    {/* Project Image from Unsplash */}
                    <div className="h-32 overflow-hidden" data-id="element-1010">
                      <img src={project.image} alt={project.name} className="w-full h-full object-cover" data-id="element-1011" />
                    </div>

                    {/* Project Info */}
                    <div className="p-3" data-id="element-1012">
                      <h4 className="font-semibold text-sm text-neutral-text mb-1" data-id="element-1013">
                        {project.name}
                      </h4>
                      <p className="text-xs text-gray-500 mb-2" data-id="element-1014">
                        {project.location}
                      </p>
                      <div className="flex items-center justify-between" data-id="element-1015">
                        <span className="text-xs text-gray-600" data-id="element-1016">
                          {project.units} Units
                        </span>
                        <button onClick={() => onProjectClick && onProjectClick(project.id)} className="text-xs font-semibold text-secondary hover:text-secondary-dark transition-colors" data-id="element-1017">
                          View Details
                        </button>
                      </div>
                    </div>
                  </div>)}
              </div>}
          </section>
        </div>

        {/* Create Ticket Modal */}
        {showCreateTicketModal && <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center" data-id="element-1018">
            <div className="bg-white w-full max-w-md rounded-t-3xl sm:rounded-2xl max-h-[85vh] overflow-y-auto" data-id="element-1019">
              <div className="sticky top-0 bg-white px-6 py-4 border-b border-gray-100 flex items-center justify-between" data-id="element-1020">
                <h2 className="text-xl font-bold text-neutral-text" data-id="element-1021">
                  Create New Ticket
                </h2>
                <button onClick={() => {
              setShowCreateTicketModal(false);
              setNewTicket({
                projectId: '',
                title: '',
                description: ''
              });
            }} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-neutral-bg rounded-full transition-colors" data-id="element-1022">
                  <X className="w-5 h-5" data-id="element-1023" />
                </button>
              </div>

              <div className="p-6 space-y-4" data-id="element-1024">
                <div data-id="element-1025">
                  <label className="block text-sm font-medium text-neutral-text mb-2" data-id="element-1026">
                    Select Project *
                  </label>
                  <select value={newTicket.projectId} onChange={e => setNewTicket({
                ...newTicket,
                projectId: e.target.value
              })} className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-secondary focus:border-transparent" data-id="element-1027">
                    <option value="" data-id="element-1028">Choose a project...</option>
                    {projects.map(project => <option key={project.id} value={project.id} data-id="element-1029">
                        {project.name} - {project.location}
                      </option>)}
                  </select>
                </div>

                <div data-id="element-1030">
                  <label className="block text-sm font-medium text-neutral-text mb-2" data-id="element-1031">
                    Request Title *
                  </label>
                  <input type="text" value={newTicket.title} onChange={e => setNewTicket({
                ...newTicket,
                title: e.target.value
              })} placeholder="e.g., Unit Reservation Request" className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-secondary focus:border-transparent" data-id="element-1032" />
                </div>

                <div data-id="element-1033">
                  <label className="block text-sm font-medium text-neutral-text mb-2" data-id="element-1034">
                    Description *
                  </label>
                  <textarea value={newTicket.description} onChange={e => setNewTicket({
                ...newTicket,
                description: e.target.value
              })} placeholder="Describe your request in detail..." rows={4} className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-secondary focus:border-transparent resize-none" data-id="element-1035" />
                </div>

                <button onClick={handleCreateTicket} disabled={!newTicket.projectId || !newTicket.title || !newTicket.description} className="w-full bg-secondary hover:bg-secondary-dark text-white font-semibold py-3 rounded-lg transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed mt-6" data-id="element-1036">
                  Create Ticket
                </button>
              </div>
            </div>
          </div>}

        <BottomNav data-id="element-1037" />
      </div>
    </div>;
}